import { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import MovieCard from '@/components/feature/MovieCard';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { movies, categories } from '@/mocks/movies';

const CATEGORY_NAMES: Record<string, string> = {};
categories.forEach((c) => { CATEGORY_NAMES[c.key] = c.name; });

type SortOption = 'default' | 'rating' | 'year' | 'title';

export default function Category() {
  const { categoryKey } = useParams<{ categoryKey: string }>();
  const [sortBy, setSortBy] = useState<SortOption>('default');

  const activeCategory = categoryKey || 'all';
  const categoryName = CATEGORY_NAMES[activeCategory] || '全部';

  const filteredMovies = useMemo(() => {
    let result = activeCategory === 'all' ? [...movies] : movies.filter((m) => m.category === activeCategory);

    switch (sortBy) {
      case 'rating':
        result.sort((a, b) => b.rating - a.rating);
        break;
      case 'year':
        result.sort((a, b) => b.year - a.year);
        break;
      case 'title':
        result.sort((a, b) => a.title.localeCompare(b.title, 'zh'));
        break;
    }
    return result;
  }, [activeCategory, sortBy]);

  const sortOptions: { key: SortOption; name: string }[] = [
    { key: 'default', name: '默认排序' },
    { key: 'rating', name: '评分最高' },
    { key: 'year', name: '最新上映' },
  ];

  const jsonLdWebPage = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `7777影院 - ${categoryName}分类`,
    description: `7777影院${categoryName}分类页面，海量${categoryName}资源免费在线观看，高清播放流畅不卡顿。`,
    url: `https://www.7777yingyuan.com/category/${activeCategory}`,
    isPartOf: { '@type': 'WebSite', name: '7777影院', url: 'https://www.7777yingyuan.com/' },
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(jsonLdWebPage)}</script>
      <Navbar />

      <main className="pt-20 min-h-screen bg-background-50">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8">
          {/* Breadcrumb */}
          <div className="py-4">
            <Breadcrumb
              items={[
                { name: '首页', path: '/' },
                { name: categoryName },
              ]}
            />
          </div>

          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
            <div>
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-50">
                {categoryName}
              </h1>
              <p className="text-sm text-foreground-500 mt-1">
                共 {filteredMovies.length} 部影片
              </p>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-foreground-500 mr-1">排序：</span>
              {sortOptions.map((opt) => (
                <button
                  key={opt.key}
                  onClick={() => setSortBy(opt.key)}
                  className={`text-xs font-medium px-3 py-1.5 rounded-full transition-colors cursor-pointer whitespace-nowrap ${
                    sortBy === opt.key
                      ? 'bg-primary-500 text-background-950'
                      : 'bg-background-100 text-foreground-400 hover:text-foreground-200 border border-background-200/50'
                  }`}
                >
                  {opt.name}
                </button>
              ))}
            </div>
          </div>

          {/* Category Tabs */}
          <div className="flex items-center gap-2 overflow-x-auto pb-4 mb-4 flex-wrap">
            {categories.map((cat) => (
              <Link
                key={cat.key}
                to={cat.key === 'all' ? '/category/all' : `/category/${cat.key}`}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                  activeCategory === cat.key
                    ? 'bg-primary-500 text-background-950'
                    : 'bg-background-100 text-foreground-400 hover:text-foreground-200 border border-background-200/50'
                }`}
              >
                <i className={`${cat.icon} text-xs`}></i>
                {cat.name}
              </Link>
            ))}
          </div>

          {/* Movie Grid */}
          <div className="pb-14">
            {filteredMovies.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5" data-product-shop>
                {filteredMovies.map((movie) => (
                  <MovieCard key={movie.id} movie={movie} variant="compact" />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-600 mb-4">
                  <i className="ri-film-line text-2xl"></i>
                </div>
                <p className="text-foreground-500">暂无该分类影片，敬请期待</p>
              </div>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </>
  );
}